import { ScoreCategory, ScoreModuleState } from './TreamsScore.types';
import type {
	ScoreModuleStatus,
	TreamsCategoryScore,
	TreamsScore,
	TreamsScoreRow,
} from './TreamsScore.types';
import { SCORE_WEIGHTS } from './config/score-weights';

const round1 = (value: number): number => Math.round(value * 10) / 10;

// Mean of each category across a set of rows. The total is left at zero on
// purpose: callers run `adjustScoresForModuleStatus`, which recomputes it from
// the live module weights.
export const aggregateRowScores = (
	rows: Pick<TreamsScoreRow, 'scores' | 'comparisonAvailable'>[],
): TreamsScore => {
	const mean = (values: number[]) =>
		values.length ? values.reduce((total, value) => total + value, 0) / values.length : 0;

	// A group with no score three months earlier must not pull the change toward zero.
	const comparable = rows.filter((row) => row.comparisonAvailable !== false);
	const deltaRows = comparable.length > 0 ? comparable : [];

	const categoryScores = Object.fromEntries(
		Object.values(ScoreCategory).map((category) => [
			category,
			{
				score: mean(rows.map((row) => row.scores[category].score)),
				delta: mean(deltaRows.map((row) => row.scores[category].delta)),
			},
		]),
	) as Record<ScoreCategory, TreamsCategoryScore>;

	return { ...categoryScores, total: { score: 0, delta: 0 } };
};

export const adjustScoresForModuleStatus = (
	scores: TreamsScore,
	status: ScoreModuleStatus,
): TreamsScore => {
	const categories = Object.keys(SCORE_WEIGHTS) as ScoreCategory[];

	const adjusted = {} as Record<ScoreCategory, TreamsCategoryScore>;

	let weightedScore = 0;
	let weightedDelta = 0;
	let totalWeight = 0;

	for (const category of categories) {
		const isEnabled = status[category] === ScoreModuleState.Enabled;
		const score = isEnabled ? scores[category].score : 0;
		const delta = isEnabled ? scores[category].delta : 0;

		adjusted[category] = { score, delta };

		const weight = SCORE_WEIGHTS[category];

		weightedScore += score * weight;
		weightedDelta += delta * weight;
		totalWeight += weight;
	}

	const total: TreamsCategoryScore =
		totalWeight === 0
			? { score: 0, delta: 0 }
			: {
					score: round1(weightedScore / totalWeight),
					delta: round1(weightedDelta / totalWeight),
				};

	return { ...adjusted, total };
};
